import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface Branch {
  id: string; code: string; name: string;
  city: string | null; province: string | null; phone: string | null;
  isActive: boolean;
}

const EMPTY = { code: '', name: '', city: '', province: '', phone: '' };

export function Branches() {
  const { can } = useAuth();
  const list = usePagedList<Branch>('/branches');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  /** Scoped to the dialog: a save failure has to stay next to the fields that caused it. */
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function openForm() {
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.post('/branches', {
        ...form,
        city: form.city || null, province: form.province || null, phone: form.phone || null,
      });
      setOpen(false);
      setNotice(`Cabang ${form.code} ditambahkan.`);
      // Back to page one: the list is newest-first, so that is where the new row landed.
      list.refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan cabang.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Cabang UDD</h2>
        <p>
          {can('branch:write')
            ? 'Cabang menjadi scope stok darah dan akun ledger.'
            : 'Anda hanya melihat cabang yang menjadi scope akun Anda.'}
        </p>
      </div>

      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari kode, nama, atau kota…"
        action={can('branch:write') ? { label: 'Tambah cabang', onClick: openForm } : undefined}
        empty="Belum ada cabang yang bisa Anda lihat."
        emptyFiltered="Tidak ada cabang yang cocok dengan pencarian."
        columns={['Kode', 'Nama', 'Kota', 'Telepon', 'Status']}
      >
        {list.items.map((r) => (
          <tr key={r.id}>
            <td className="mono">{r.code}</td>
            <td>{r.name}</td>
            <td className="muted">{r.city ?? '—'}</td>
            <td className="mono muted">{r.phone ?? '—'}</td>
            <td>
              <span className={r.isActive ? 'pill accent' : 'pill'}>
                {r.isActive ? 'aktif' : 'nonaktif'}
              </span>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal
        open={open}
        title="Tambah cabang"
        description="Kode dipakai di nomor kantong dan nomor donasi, jadi pilih yang tidak akan berubah."
        onClose={() => setOpen(false)}
        onSubmit={create}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan cabang'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}
          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="b-code">Kode</label>
              <input id="b-code" value={form.code} required placeholder="UDD-BDG"
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="b-name">Nama</label>
              <input id="b-name" value={form.name} required placeholder="UDD PMI Kota Bandung"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="b-city">Kota</label>
              <input id="b-city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="b-province">Provinsi</label>
              <input id="b-province" value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="b-phone">Telepon</label>
              <input id="b-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
